<template>
  <div>
    <v-container fluid>
      <v-card class="pb-10">
        <v-btn
          v-show="true"
          color="green"
          fab
          dark
          absolute
          top
          right
          @click.stop="openNewMarketDialog()"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>

        <v-card-title class="pt-10"
          >Market Dates
          <v-spacer></v-spacer>
          <v-menu bottom right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined color="grey darken-2" v-bind="attrs" v-on="on">
                <span>{{ view }}</span>
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="toggleView('Calendar View')">
                <v-list-item-title>Calendar View</v-list-item-title>
              </v-list-item>
              <v-list-item @click="toggleView('List View')">
                <v-list-item-title>List View</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>

        <v-row class="fill-height" v-if="view == 'Calendar View'">
          <v-col>
            <v-sheet height="64">
              <v-toolbar flat>
                <v-btn
                  outlined
                  class="mr-4"
                  color="grey darken-2"
                  @click="setToday"
                >
                  Today
                </v-btn>
                <v-btn fab text small color="grey darken-2" @click="prev">
                  <v-icon small>
                    mdi-chevron-left
                  </v-icon>
                </v-btn>
                <v-btn fab text small color="grey darken-2" @click="next">
                  <v-icon small>
                    mdi-chevron-right
                  </v-icon>
                </v-btn>
                <v-toolbar-title class="ml-4">
                  {{
                    this.$refs.calendar
                      ? this.$refs.calendar.title
                      : calendarTitle
                  }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
            </v-sheet>
            <v-sheet height="600">
              <v-calendar
                ref="calendar"
                v-model="calendarFocusDate"
                color="primary"
                :events="eventDates"
                :event-color="getEventColor"
                type="month"
                @click:event="showEvent"
                @click:date="setNewDateDialog"
                @change="calendarUpdate"
              ></v-calendar>
            </v-sheet>
          </v-col>
        </v-row>

        <keep-alive>
          <v-data-table
            v-if="view == 'List View'"
            :headers="tableHeaders"
            :items="tableData"
            :options.sync="options"
            :server-items-length="totalItems"
            :loading="isLoading"
            :must-sort="true"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :footer-props="{
              'items-per-page-options': [25, 50, 100]
            }"
            :item-class="itemRowBackground"
            @click:row="handleRowClick"
          >
            <!-- Market Date -->
            <template v-slot:item.market_date="{ item }">
              {{ formatDate(item.market_date) }}
            </template>

            <!-- Actions -->
            <template v-slot:item.actions="{ item }">
              <v-menu bottom left>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(action, i) in tableActions"
                    :key="i"
                    @click="handleDynamicFunctionCall(action, item)"
                  >
                    <v-list-item-title>{{ action.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </keep-alive>
      </v-card>

      <MarketDialog
        :dialogVisible="isNewMarketDialogVisible"
        :selectedDates="
          preselectedNewMarketDate ? [preselectedNewMarketDate] : []
        "
        @close="closeNewMarketDialog"
        @create="createMarkets"
      />

      <ConfirmationDialog
        :dialogVisible="confirmationDialog.show"
        :text="confirmationDialog.text"
        :heading="confirmationDialog.heading"
        @cancel="closeConfirmationDialog"
        @confirm="
          handleFunctionCall(
            confirmationDialog.action,
            confirmationDialog.params
          )
        "
      />

      <AlertDialog
        :dialogVisible="alertDialog.show"
        :text="alertDialog.text"
        :heading="alertDialog.heading"
        @close="alertDialog.show = false"
      />
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import AlertDialog from "../components/AlertDialog";
import ConfirmationDialog from "../components/ConfirmationDialog";
import MarketDialog from "../components/NewMarketDialog";
import { formatDate } from "../js/helpers";

export default {
  data() {
    return {
      isLoading: true,
      totalItems: 0,
      options: {
        itemsPerPage: 25,
        page: 1
      },
      sortBy: "market_date",
      sortDesc: true,
      items: [],
      selectedMarketId: false,
      headers: [
        // { text: "ID", value: "id" },
        {
          text: "Market Date",
          align: "right",
          value: "market_date",
          sortable: true,
          width: "80px"
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "right"
          //width: "50px"
        }
      ],
      actions: [
        {
          title: "Market Details",
          function: "goToMarket",
          params: ["market_date"]
        },
        {
          title: "Delete Market",
          function: "openDeleteConfirmation",
          params: ["id"]
        }
      ],
      isNewMarketDialogVisible: false,
      calendarFocusDate: "",
      preselectedNewMarketDate: null,
      view: "Calendar View",
      confirmationDialog: {
        show: false,
        heading: "Delete Market",
        text: "",
        action: "",
        params: ""
      },
      alertDialog: {
        show: false,
        heading: "",
        text: ""
      },
      calendarTitle: ""
    };
  },
  components: {
    AlertDialog,
    ConfirmationDialog,
    MarketDialog
  },
  watch: {
    options: {
      handler() {
        console.log("options change");
        this.fetchMarkets();
      },
      deep: true
    }
  },
  methods: {
    fetchMarkets() {
      const today = new Date();
      const todayYear = today.getFullYear();

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      let order = sortDesc.length === 1 && sortDesc[0] == true ? "desc" : "asc";

      let query =
        `/wisdom-markets/v1/markets/?` +
        `page=${page}` +
        `&orderby=${sortBy}` +
        `&order=${order}` +
        `&per_page=${itemsPerPage}`;

      this.isLoading = true;

      axios
        .get(query, {
          headers: {
            Authorization: `Bearer ${this.$store.state.accessToken}`
          }
        })
        .then(response => {
          let items = response.data;
          const totalItems = parseInt(response.headers["x-wp-total"]);
          console.warn({ totalItems });

          this.items = items;
          this.totalItems = totalItems;
          this.isLoading = false;
        })
        .catch(error => {
          console.log(error);
          // this.isLoading = false;
        });
    },
    deleteMarket(id) {
      this.$store.commit("isSaving", true);

      axios({
        method: "DELETE",
        url: `/wisdom-markets/v1/markets/${this.selectedMarket.id}`,
        headers: {
          Authorization: `Bearer ${this.$store.state.accessToken}`
        }
      })
        .then(response => {
          this.$store.commit("isSaving", false);
          this.confirmationDialog.show = false;
          this.deleteMarketFromList(id, this.selectedMarket.market_date);
          this.selectedMarketId = false;
        })
        .catch(error => {
          console.log(error);
          let message = error;
          if (error.response.data.message)
            message = error.response.data.message;

          this.$store.commit("isSaving", false);
          this.alertDialog.heading = "Error!";
          this.alertDialog.text = `Cannot delete booking: ${message}`;
          this.alertDialog.show = true;
        });
    },
    deleteMarketFromList(id, date) {
      // remove the market (by id) from the items list, then update the store picker list

      let index = this.items.findIndex(item => item.id === id);

      if (index !== -1) this.items.splice(index, 1);

      this.$store.commit("deletePickerAvailableMarketsDate", date); // so after submission the picker will display them as greyed out
    },
    formatDate(date) {
      return formatDate(date);
    },
    goToMarket(date) {
      this.$router.push({ name: "market", params: { date: date } });
    },
    showEvent(data) {
      this.goToMarket(data.day.date);
    },
    openNewMarketDialog() {
      this.isNewMarketDialogVisible = true;
    },
    closeNewMarketDialog() {
      this.isNewMarketDialogVisible = false;
      this.preselectedNewMarketDate = null;
    },
    handleFunctionCall(action, params) {
      this[action](...params);
    },
    handleDynamicFunctionCall(action, item) {
      const params = action.params.map(param => {
        return item[param];
      });
      this[action.function](...params);
    },
    getEventColor(event) {
      const today = new Date();
      const marketDate = new Date(`${event.start} 23:59:59`);
      return marketDate > today ? "green" : "grey";
    },
    setToday() {
      this.calendarFocusDate = "";
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    calendarUpdate: async function({ start, end }) {
      const year = start.year;

      if (!this.$store.state.fetchedPickerMarketYears.includes(year)) {
        var res = await axios
          .get(`/wisdom-markets/v1/markets/`, {
            headers: {
              Authorization: `Bearer ${this.$store.state.accessToken}`
            },
            params: {
              from: `${year}-01-01`,
              to: `${year}-12-31`
            }
          })
          .then(response => {
            console.log("got response", response);

            const dates = response.data.map(m => m.market_date);
            this.$store.commit("updatePickerAvailableMarketsDates", {
              dates,
              year
            });
          });
      }

      return;
    },
    setNewDateDialog({ date }) {
      this.preselectedNewMarketDate = date;
      this.openNewMarketDialog();
    },
    createMarkets(markets) {
      // insert the dates in the items list, is the correct position, then update the store picker list

      const dates = markets.map(m => {
        let index = this.items.findIndex(
          item => item.market_date < m.market_date
        );

        if (index !== -1) this.items.splice(index, 1, m);
        else this.items.push(m);

        return m.market_date;
      });

      this.$store.commit("updatePickerAvailableMarketsDates", { dates }); // so after submission the picker will display them as greyed out
    },
    itemRowBackground(item) {
      const today = new Date();
      const marketDate = new Date(`${item.market_date} 23:59:59`);
      return marketDate >= today ? "is-future" : "is-past";
    },
    handleRowClick(item) {
      this.goToMarket(item.market_date);
    },
    openDeleteConfirmation(id) {
      this.selectedMarketId = id;
      this.confirmationDialog.text = `Are you sure you want to delete the Market on <strong>${formatDate(
        this.selectedMarket.market_date
      )}?`;
      this.confirmationDialog.show = true;
      this.confirmationDialog.action = "deleteMarket";
      this.confirmationDialog.params = [id];
    },
    closeConfirmationDialog(payload) {
      this.confirmationDialog.show = false;
    },
    toggleView(view) {
      this.view = view;
      if (view == "Calendar View") {
        // we just we need to this because when we switch from back from list to calendar the title is blank (because $refs.calendar is not immediately defined), by changing calendarTitle it forces that part of the template to udpate, even though calendarTitle is never really used.
        this.$nextTick(function() {
          this.calendarTitle = this.$refs.calendar.title; // alternatively this coul dhave been added to the vue updated hook
        });
      }
    }
  },
  mounted() {
    this.calendarTitle = this.$refs.calendar.title; // when we press browser back, trigger the title in the template to update
  },
  computed: {
    tableHeaders() {
      return this.headers;
    },
    tableData() {
      return this.items;
    },
    tableActions() {
      return this.actions;
    },
    eventDates() {
      return this.$store.state.availablePickerMarketsDates.map(market_date => {
        return {
          name: "MARKET DAY",
          start: market_date
        };
      });
    },
    selectedMarket() {
      return this.items.find(market => this.selectedMarketId == market.id);
    }
  }
};
</script>

<style>
.is-past {
  background-color: rgb(250, 250, 250);
  color: grey;
}
.is-future {
  /* background-color: rgb(114,114,67) */
}
</style>
